import { Component } from '@theme/component';

/**
 * @typedef {Object} ModalRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLButtonElement} closeButton - The close button
 * @property {HTMLElement} [dragHandle] - The drag handle for mobile drawer
 * @property {HTMLElement} [content] - The scrollable content area
 */

/**
 * Generic modal/drawer component.
 * Desktop: Centered modal behavior.
 * Mobile: Bottom drawer with touch gestures.
 *
 * @extends {Component<ModalRefs>}
 */
class NcnModal extends Component {
  requiredRefs = ['dialog', 'closeButton'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #startY = 0;

  /** @type {number} */
  #currentY = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} - Drag threshold in pixels for closing the drawer */
  #dragThreshold = 100;

  /** @type {number | null} - Safety timeout for close animation fallback */
  #closeTimeout = null;

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia('(max-width: 749px)');

  /** @type {boolean} */
  #prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const { closeButton, dragHandle, dialog } = this.refs;

    // Close button handler
    closeButton?.addEventListener('click', this.#handleClose, { signal });

    // Mobile drag handlers
    if (dragHandle) {
      dragHandle.addEventListener('touchstart', this.#handleTouchStart, { passive: true, signal });
      dragHandle.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
      dragHandle.addEventListener('touchend', this.#handleTouchEnd, { signal });
    }

    // Backdrop click to close
    dialog?.addEventListener('click', this.#handleBackdropClick, { signal });

    // ESC key handling
    dialog?.addEventListener('cancel', this.#handleCancel, { signal });

    // Listen for reduced motion preference changes
    window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', this.#handleMotionPreferenceChange, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    super.disconnectedCallback();
  }

  /**
   * Handle reduced motion preference changes
   * @param {MediaQueryListEvent} event
   */
  #handleMotionPreferenceChange = (event) => {
    this.#prefersReducedMotion = event.matches;
  };

  /**
   * Shows the modal (public API)
   */
  showModal() {
    const { dialog, closeButton } = this.refs;
    if (!dialog) {
      console.warn('NcnModal: dialog element not found');
      return;
    }
    if (dialog.open) return;

    // Store scroll position and lock body
    this.#previousScrollY = window.scrollY;

    try {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      document.body.style.width = '100%';

      dialog.showModal();

      // Explicitly focus the close button for better accessibility
      if (closeButton) {
        closeButton.focus();
      }
    } catch (error) {
      console.error('NcnModal: Failed to open modal', error);
    }
  }

  /**
   * Closes the modal
   */
  closeModal() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    if (this.#prefersReducedMotion) {
      this.#finalizeClose();
      return;
    }

    dialog.classList.add('ncn-modal--closing');

    // Use the appropriate end event: transitionend for mobile slide, animationend for desktop keyframes
    const endEvent = this.#mobileQuery.matches ? 'transitionend' : 'animationend';
    dialog.addEventListener(endEvent, () => this.#finalizeClose(), { once: true });

    // Safety fallback in case the event never fires
    this.#closeTimeout = setTimeout(() => this.#finalizeClose(), 400);
  }

  /**
   * Finishes the close process
   */
  #finalizeClose() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    // Clear safety timeout if the event fired first
    if (this.#closeTimeout) {
      clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }

    try {
      dialog.classList.remove('ncn-modal--closing');
      dialog.close();

      // Temporarily override smooth scrolling so the scroll restoration is instant
      const scrollY = this.#previousScrollY;
      document.documentElement.style.scrollBehavior = 'auto';

      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);

      document.documentElement.style.scrollBehavior = '';

      // Return focus to trigger element via data-trigger attribute
      const triggerId = this.dataset.trigger;
      if (triggerId) {
        const triggerElement = document.getElementById(triggerId);
        if (triggerElement) {
          triggerElement.focus({ preventScroll: true });
        }
      }
    } catch (error) {
      console.error('NcnModal: Failed to close modal', error);
    }
  }

  /**
   * Handle close button click
   */
  #handleClose = () => {
    this.closeModal();
  };

  /**
   * Handle ESC key (dialog cancel event)
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeModal();
  };

  /**
   * Handle backdrop click
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    const { dialog } = this.refs;
    if (event.target === dialog) {
      this.closeModal();
    }
  };

  /**
   * Handle touch start for drag gesture
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (!this.#mobileQuery.matches) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#startY = touch.clientY;
    this.#currentY = touch.clientY;
    this.#isDragging = true;

    const { dialog } = this.refs;
    if (dialog) {
      dialog.classList.add('ncn-modal--dragging');
    }
  };

  /**
   * Handle touch move for drag gesture
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (!this.#isDragging || !this.#mobileQuery.matches) return;

    const touch = event.touches[0];
    if (!touch) return;

    const deltaY = touch.clientY - this.#startY;

    // Only allow dragging down
    if (deltaY < 0) {
      this.#currentY = this.#startY;
      return;
    }

    this.#currentY = touch.clientY;

    // Apply transform to dialog
    const { dialog } = this.refs;
    if (dialog) {
      dialog.style.transform = `translateY(${deltaY}px)`;
    }

    // Prevent scrolling while dragging
    event.preventDefault();
  };

  /**
   * Handle touch end for drag gesture
   */
  #handleTouchEnd = () => {
    if (!this.#isDragging || !this.#mobileQuery.matches) return;

    this.#isDragging = false;

    const { dialog } = this.refs;
    if (!dialog) return;

    const deltaY = this.#currentY - this.#startY;

    // Close if dragged past threshold
    if (deltaY > this.#dragThreshold) {
      dialog.classList.remove('ncn-modal--dragging');
      this.closeModal();
      requestAnimationFrame(() => {
        dialog.style.transform = '';
      });
      return;
    }

    dialog.classList.remove('ncn-modal--dragging');

    // Reset transform
    dialog.style.transform = '';
  };
}

if (!customElements.get('ncn-modal')) {
  customElements.define('ncn-modal', NcnModal);
}
